<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-receipt me-2"></i>Invoices Management
      </h4>
      <div class="d-flex">
        <button v-if="profile.role !== 'user'" class="btn btn-primary" @click="openInvoiceModal">
          <i class="bi bi-plus-circle me-1"></i> New Invoice
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loader -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading invoices...</p>
      </div>

      <!-- Content -->
      <div v-if="!loading" class="invoices-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ filteredInvoices.length }}</span>
            <span class="text-muted">Invoices</span>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search invoice" class="mb-0 w-auto" />
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>#</th>
                <th>CODE</th>
                <th>CUSTOMER</th>
                <th>STORE</th>
                <th>CREATED BY</th>
                <th>DUE DATE</th>
                <th>CREATED</th>
                <th>STATUS</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, index) in paginatedInvoices" :key="row.id" class="invoice-row" :class="{'bg-light': index % 2 === 0}">
                <td>{{ index + 1 + (dataTablePage -1)*dataTablePerPage }}</td>
                <td>{{ row.code || row.id }}</td>
                <td>{{ row.customer.name }}</td>
                <td>{{ row.store.name }}</td>
                <td>{{ row.user.first_name + ' ' + row.user.last_name }}</td>
                <td>{{ formatDate(row.due_date) }}</td>
                <td>{{ formatDate(row.created_at) }}</td>
                <td>
                  <Badge pill :type="getPillByStatus(row.status)">{{ row.status }}</Badge>
                </td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-primary" @click="invoiceDetails(row.id)">
                    <i class="bi bi-eye"></i> Details
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination v-model="dataTablePage" :pages="totalPages" :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

      </div>
    </div>
  </Card>

  <!-- Add Invoice Modal -->
  <Modal :open="invoiceModal" @closed="invoiceModal=false" dialog-size="modal-lg">
    <div class="modal-header bg-light">
      <h5 class="modal-title m-0"><i class="bi bi-plus-circle me-2"></i>Add Invoice</h5>
      <button type="button" class="btn-close" @click="invoiceModal=false"></button>
    </div>
    <div class="modal-body">
      <form @submit.prevent="submitInvoice">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Customer</label>
            <select class="form-select" v-model="invoiceForm.customer_id" data="">
              <option disabled value="">Select customer</option>
              <option v-for="c in customers" :key="c.id" :value="c.id">{{c.name}}</option>
            </select>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Store</label>
            <select class="form-select" v-model="invoiceForm.store_id" data="">
              <option disabled value="">Select store</option>
              <option v-for="s in stores" :key="s.id" :value="s.id">{{s.name}}</option>
            </select>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Discount</label>
            <div class="input-group">
              <span class="input-group-text">%</span>
              <input type="number" min="0" class="form-control" v-model.number="invoiceForm.discount">
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Due Date</label>
            <input type="date" class="form-control" v-model="invoiceForm.due_date">
          </div>
        </div>
        <div class="mb-3">
          <label class="form-label fw-bold">Notes</label>
          <textarea class="form-control" rows="3" v-model="invoiceForm.notes" placeholder="Optional notes"></textarea>
        </div>
      </form>
    </div>
    <div class="modal-footer bg-light">
      <button class="btn btn-outline-secondary" @click="invoiceModal=false"><i class="bi bi-x-circle me-1"></i>Cancel</button>
      <button class="btn btn-primary" @click="submitInvoice"><i class="bi bi-plus-circle me-1"></i>Create Invoice</button>
    </div>
  </Modal>
  <!-- End Modal -->
</template>

<script>
import invoiceService from "@/api/invoiceService";
import customerService from "@/api/customerService";
import storeService from "@/api/storeService";
import router from "@/router";
import { decrypt } from "@/utils/crypto";
import Pagination from "@/components/pagination/Pagination.vue";
import Modal from "@/components/modal/Modal";
import formatDate from "@/utils/formatDate";

export default {
  name: "Invoices",
  components: { Pagination, Modal },
  data() {
    return {
      profile: {},
      loading: true,
      dataTable: [],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      invoiceModal: false,
      invoiceForm: {
        customer_id: '',
        store_id: '',
        discount: 0,
        notes: '',
        due_date: ''
      },
      customers: [],
      stores: [],
      organization:{}
    }
  },
  methods: {
    formatDate,
    getPillByStatus(status){
      return status === 'complete' ? 'success' : status === 'cancelled' ? 'danger' : 'warning';
    },
    async getInvoices(){
      try{
        this.dataTable = await invoiceService.getInvoices(this.invoiceForm.store_id);
      }catch(e){ console.error(e);} finally { this.loading = false; }
    },
    async loadDropdowns(){
      try{
        const customersData = await customerService.getCustomers({ per_page: 1000 });
        this.customers = Array.isArray(customersData?.data) ? customersData.data.filter(c => c != null) : [];
        const storesData = await storeService.getStores();
        this.stores = Array.isArray(storesData?.data) ? storesData.data.filter(s => s != null) : (Array.isArray(storesData) ? storesData.filter(s => s != null) : []);
      }catch(e){console.error(e);}
    },
    invoiceDetails(id){ router.push('/invoice/' + id) },
    openInvoiceModal(){
      this.invoiceForm={customer_id:'',store_id:'',discount:0,notes:'',due_date:''};
      this.invoiceModal=true;
    },
    async submitInvoice(){
      try{
        const payload = { ...this.invoiceForm };
        await invoiceService.createInvoice(payload);
        this.invoiceModal = false;
        await this.getInvoices();
      }catch(e){ console.error(e); }
    }
  },
  computed:{
    filteredInvoices(){
      if(!this.dataTableSearch) return this.dataTable;
      const term = this.dataTableSearch.toLowerCase();
      return this.dataTable.filter(i =>
          (i.code && i.code.toLowerCase().includes(term)) ||
          (i.customer && i.customer.name.toLowerCase().includes(term)) ||
          (i.status && i.status.toLowerCase().includes(term))
      );
    },
    paginatedInvoices(){
      const start = (this.dataTablePage-1)*this.dataTablePerPage;
      return this.filteredInvoices.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      return Math.ceil(this.filteredInvoices.length/ this.dataTablePerPage) || 1;
    }
  },
  watch:{ dataTablePerPage(){ this.dataTablePage = 1; } },
  async created(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
    await Promise.all([this.getInvoices(), this.loadDropdowns()]);
  }
}
</script>

<style scoped>
.invoices-content{ padding:0.5rem; }
.table th{ font-weight:600; text-transform:uppercase; font-size:0.7rem; letter-spacing:0.5px; color:#6b7280; border-top:none; padding:1rem 1.5rem; background:#f9fafb; border-bottom:1px solid #e9ecef; }
.table td{ padding:1rem 1.5rem; vertical-align:middle; border-color:#e9ecef; color:#4b5563; }
.invoice-row{ transition:all 0.2s ease; }
.invoice-row:hover{ background:#f8f9fa; transform:translateY(-2px); box-shadow:0 4px 6px -1px rgba(0,0,0,0.05); }
</style>
